import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, CircularProgress, Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Warning as WarningIcon,
  SwapHoriz as SwapHorizIcon, CallMerge as MergeIcon,
  Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { PageHeader, SectionTitle, Card, Badge } from '../../design-system';
import { formatPercentage } from '../../utils/formatters';
import {
  useGetEntityNetwork, useCreateEntity, useUpdateEntity,
  useSetEntityOwners, useGetBeneficialOwners,
  useGetEntityParties, useSetEntityParties,
  useGetDuplicateSuggestions, useMergeEntities, useDismissDuplicate,
  useSetEntityChartVisibility,
} from '../../api/entities';
import { mutate } from 'swr';
import EntityNetworkGraph from './EntityNetworkGraph';
import { useConfirmAction } from './confirmactionhandler';
import SnackbarContext from '../../context/snackbar-context';
import UserProfileContext from '../../context/userprofilecontext';
import { useChannelSubscription } from '../../hooks/useAppSyncSubscription';
import styles from './EntityOwnershipManager.module.css';

const KINDS = ['Person', 'Business', 'Trust', 'Estate'];
const KIND_CLASS = { Person: 'kindPerson', Business: 'kindBusiness', Trust: 'kindTrust', Estate: 'kindEstate' };

// Solid type swatch — same palette as the graph nodes (white text on the solid
// color) so an entity type reads identically on the list and in the chart.
function KindChip({ kind }) {
  return <span className={`${styles.kindChip} ${styles[KIND_CLASS[kind]] || ''}`}>{kind}</span>;
}
const EMPTY_FORM = { kind: 'Business', display_name: '', abbreviation: '', dba_names: [] };
// Mirrors MAX_ABBREVIATION_LEN in the shared layer's ownership.py.
const MAX_ABBREVIATION_LEN = 32;
const ROLES_BY_KIND = {
  Trust: [['grantor', 'Grantor'], ['trustee', 'Trustee'], ['beneficiary', 'Beneficiary']],
  Estate: [['decedent', 'Decedent'], ['personal_representative', 'Personal representative'], ['beneficiary', 'Beneficiary']],
};

// Percent in the model is a 0–1 decimal; the UI shows whole-number percents.
const pctToInput = (pct) => (pct === '' || pct == null ? '' : Math.round((Number(pct) || 0) * 100));
const inputToPct = (val) => (val === '' ? 0 : (Number(val) || 0) / 100);
const apiMessage = (e) => (e && (e.message || e.toString())) || 'Something went wrong.';

export default function EntityOwnershipManager() {
  const { data, error, isLoading } = useGetEntityNetwork();
  const createEntity = useCreateEntity();
  const updateEntity = useUpdateEntity();
  const setEntityOwners = useSetEntityOwners();
  const setEntityParties = useSetEntityParties();
  const { data: dupData } = useGetDuplicateSuggestions();
  const mergeEntitiesFn = useMergeEntities();
  const dismissDuplicateFn = useDismissDuplicate();
  const { openSnackbarError } = useContext(SnackbarContext) || {};

  // Live-refresh: agent-driven entity writes (add/merge/unmerge/remove-name via
  // Livi's tools) publish a 'caseDetails' AppSync event keyed on the active
  // case_id. Revalidate the network + duplicate suggestions on event so the page
  // reflects the change without a manual refresh — mirrors CaseMaintenanceScreen.
  // (Local REST edits already revalidate optimistically in api/entities.js.)
  const { userProfile } = useContext(UserProfileContext) || {};
  const activeCaseId = userProfile?.currentCase?.caseID || null;
  useChannelSubscription(
    activeCaseId,
    'caseDetails',
    () => {
      mutate('ENTITY_NETWORK');
      mutate('ENTITY_DUP_SUGGESTIONS');
    },
    { enabled: !!activeCaseId },
  );

  const entities = data?.entities || [];
  const edges = data?.edges || [];
  const entitiesById = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e])), [entities]);
  const suggestions = dupData?.suggestions || [];

  const [selectedId, setSelectedId] = useState(null);
  const selected = selectedId ? entitiesById[selectedId] : null;
  const [dialog, setDialog] = useState(null);
  const [errMsg, setErrMsg] = useState(null);
  const [dupBusy, setDupBusy] = useState(null);

  // doMerge receives a RESOLVED payload {keeper, duplicate, moves, pairKey} — the
  // user may have swapped which side survives, so direction comes from the UI.
  const doMerge = async (p) => {
    setDupBusy(p.pairKey); setErrMsg(null);
    try {
      await mergeEntitiesFn(p.keeper.id, p.duplicate.id);
      if (selectedId === p.duplicate.id) setSelectedId(p.keeper.id);
    } catch (e) { setErrMsg(apiMessage(e)); }
    finally { setDupBusy(null); }
  };
  const { handleConfirmAction: confirmMerge, ConfirmDialogElement: MergeConfirm } = useConfirmAction(doMerge, {
    title: 'Merge duplicate entity',
    entityNamePrefix: 'You are about to merge and hide this entity:',
    getEntityName: (p) => p?.duplicate?.display_name,
    confirmMessage: 'Its accounts, ownership and roles move to the kept entity. The original is preserved as a hidden lineage record, not deleted.',
    proceedButtonLabel: 'Merge',
    destructive: true,
    list_element: MergeImpact,
  });

  const handleDismiss = async (s) => {
    const pairKey = [s.keeper.id, s.duplicate.id].sort().join('|');
    setDupBusy(pairKey); setErrMsg(null);
    try {
      await dismissDuplicateFn(s.keeper.id, s.duplicate.id);
    } catch (e) { setErrMsg(apiMessage(e)); }
    finally { setDupBusy(null); }
  };

  // User-initiated merge for a duplicate the detector didn't flag. Routes through
  // the same themed confirm + backend guards as a suggested merge.
  const [mergeOpen, setMergeOpen] = useState(false);
  const startManualMerge = (keepId, dupId) => {
    setMergeOpen(false);
    const keeper = entitiesById[keepId];
    const duplicate = entitiesById[dupId];
    if (!keeper || !duplicate) return;
    confirmMerge({ keeper, duplicate, moves: undefined, pairKey: [keepId, dupId].sort().join('|') });
  };

  const openCreate = () => setDialog({ mode: 'create', form: { ...EMPTY_FORM }, id: null });
  const openEdit = (e) => setDialog({ mode: 'edit', form: { kind: e.kind, display_name: e.display_name || '', abbreviation: e.abbreviation || '', dba_names: e.dbas || [] }, id: e.id });

  // Optimistic: validate, close immediately, then persist in the background — a
  // failed write rolls the cache back (the hooks) and surfaces a toast. Edit is
  // optimistic via useUpdateEntity; create appears on the network refetch.
  const saveDialog = () => {
    const f = dialog.form;
    if (!f.display_name.trim()) { openSnackbarError?.('Name is required.'); return; }
    const name = f.display_name.trim();
    const { mode, id } = dialog;
    setErrMsg(null);
    setDialog(null);
    // DBAs are a business/trust/estate concept; a Person never carries them.
    const dba_names = f.kind === 'Person' ? [] : (f.dba_names || []);
    // Always sent (even empty) so clearing the field clears the stored prop.
    const abbreviation = (f.abbreviation || '').trim();
    const op = mode === 'create'
      ? createEntity({ kind: f.kind, display_name: name, name, dba_names, abbreviation })
      : updateEntity(id, { display_name: name, name, kind: f.kind, dba_names, abbreviation });
    Promise.resolve(op).catch((e) => openSnackbarError?.(apiMessage(e)));
  };

  // Entity hard-delete is deactivated (ADR-002 R1): entities are auditable case
  // records. Duplicates are consolidated via merge (above), never deleted.

  // View-only toggle: show/hide an entity on the ownership graph. Optimistic —
  // the cached flag flips instantly (no round-trip wait), then persists and rolls
  // back on error. No forensic effect, just the chart.
  const setChartVisibility = useSetEntityChartVisibility();
  const toggleChartVisibility = (e, ev) => {
    ev.stopPropagation();
    setErrMsg(null);
    setChartVisibility(e.id, !e.hide_from_chart).catch((err) => setErrMsg(apiMessage(err)));
  };

  const selectedOwners = useMemo(
    () => edges.filter((ed) => ed.owned_id === selectedId).map((ed) => ({ owner_id: ed.owner_id, pct: ed.pct })),
    [edges, selectedId],
  );

  if (error) {
    return (
      <div className={styles.page}>
        <PageHeader title="Entities & Ownership" />
        <Card padding="lg"><div className={styles.error}>Couldn’t load the entity network. If this case hasn’t been migrated yet, no entities will appear until it is.</div></Card>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Entities & Ownership"
        subtitle="People, businesses, trusts and estates"
        actions={(
          <>
            <Button variant="text" startIcon={<MergeIcon />} onClick={() => setMergeOpen(true)} disabled={entities.length < 2}>Merge entities</Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add entity</Button>
          </>
        )}
      />

      {errMsg && <Card padding="md"><div className={styles.error}>{errMsg}</div></Card>}

      <DuplicateSuggestions
        suggestions={suggestions}
        busyKey={dupBusy}
        onMerge={confirmMerge}
        onDismiss={handleDismiss}
      />

      <div className={styles.layout}>
        <section className={styles.listCol}>
          <SectionTitle title={`Entities (${entities.length})`} />
          <Card padding="none">
            {isLoading ? (
              <div className={styles.loading}><CircularProgress size={22} /></div>
            ) : (
              <ul className={styles.entityList}>
                {entities.map((e) => (
                  <li
                    key={e.id}
                    className={`${styles.entityRow} ${selectedId === e.id ? styles.entityRowActive : ''} ${e.hide_from_chart ? styles.entityRowHidden : ''}`}
                    onClick={() => setSelectedId(e.id)}
                  >
                    <div className={styles.entityMain}>
                      <span className={styles.entityName}>{e.display_name || '(unnamed)'}</span>
                      {e.dbas?.length > 0 && <span className={styles.entityDba}>d/b/a {e.dbas.join(', ')}</span>}
                      {e.akas?.length > 0 && <span className={styles.entityAka}>also known as {e.akas.join(', ')}</span>}
                      <span className={styles.entityMeta}>
                        <KindChip kind={e.kind} />
                        {e.case_role && <Badge variant="default" size="sm">{e.case_role}</Badge>}
                      </span>
                    </div>
                    <div className={styles.entityActions}>
                      <IconButton
                        size="small"
                        aria-label={e.hide_from_chart ? 'Show on chart' : 'Hide from chart'}
                        title={e.hide_from_chart ? 'Hidden from the chart — click to show' : 'Shown on the chart — click to hide'}
                        onClick={(ev) => toggleChartVisibility(e, ev)}
                      >
                        {e.hide_from_chart ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                      </IconButton>
                      <IconButton size="small" aria-label="Edit entity" onClick={(ev) => { ev.stopPropagation(); openEdit(e); }}><EditIcon fontSize="small" /></IconButton>
                    </div>
                  </li>
                ))}
                {!entities.length && <li className={styles.emptyRow}>No entities yet — add one to start.</li>}
              </ul>
            )}
          </Card>
        </section>

        <section className={styles.detailCol}>
          <SectionTitle title="Ownership network" />
          <EntityNetworkGraph entities={entities} edges={edges} selectedId={selectedId} onSelectEntity={setSelectedId} />
          {selected ? (
            selected.kind === 'Person' ? (
              <Card padding="lg">
                <div className={styles.detailHeader}>
                  <span className={styles.detailTitle}>{selected.display_name}</span>
                  <KindChip kind="Person" />
                  {selected.case_role && <Badge variant="default" size="sm">{selected.case_role}</Badge>}
                </div>
                {selected.akas?.length > 0 && <div className={styles.entityAka}>also known as {selected.akas.join(', ')}</div>}
                <div className={styles.hint}>A person is a natural owner and can’t itself be owned. To record that this person owns a business, trust, or estate, select that entity and add this person as an owner.</div>
              </Card>
            ) : (
              <OwnershipDetail
                key={selected.id}
                entity={selected}
                entities={entities}
                entitiesById={entitiesById}
                initialOwners={selectedOwners}
                onSaveOwners={(owners) => setEntityOwners(selected.id, owners)}
                onSaveParties={(parties) => setEntityParties(selected.id, parties)}
                onError={openSnackbarError}
              />
            )
          ) : (
            <Card padding="lg"><div className={styles.hint}>Select an entity to edit who owns it and preview its ultimate beneficial owners.</div></Card>
          )}
        </section>
      </div>

      <Dialog open={!!dialog} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        {dialog && (
          <>
            <DialogTitle>{dialog.mode === 'create' ? 'Add entity' : 'Edit entity'}</DialogTitle>
            <DialogContent className={styles.dialogContent}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="entity-kind-label">Kind</InputLabel>
                <Select
                  labelId="entity-kind-label"
                  label="Kind"
                  value={dialog.form.kind}
                  onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, kind: e.target.value } })}
                >
                  {KINDS.map((k) => <MenuItem key={k} value={k}>{k}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField
                label="Name"
                fullWidth
                margin="dense"
                value={dialog.form.display_name}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, display_name: e.target.value } })}
                autoFocus
              />
              <TextField
                label="Abbreviation"
                fullWidth
                margin="dense"
                value={dialog.form.abbreviation}
                onChange={(e) => setDialog({ ...dialog, form: { ...dialog.form, abbreviation: e.target.value } })}
                placeholder="e.g. J"
                inputProps={{ maxLength: MAX_ABBREVIATION_LEN }}
                helperText="Optional short form for on-screen reports (Title, Source). PDF and Excel exports always use the full name. Leave blank to show the full name everywhere."
              />
              {dialog.form.kind !== 'Person' && (
                <Autocomplete
                  multiple
                  freeSolo
                  options={[]}
                  value={dialog.form.dba_names || []}
                  onChange={(e, val) => setDialog({
                    ...dialog,
                    form: { ...dialog.form, dba_names: val.map((v) => String(v).trim()).filter(Boolean) },
                  })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Trade / d/b/a names"
                      margin="dense"
                      placeholder="Type a name, press Enter"
                      helperText="Doing-business-as names this entity operates under"
                    />
                  )}
                />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDialog(null)}>Cancel</Button>
              <Button variant="contained" onClick={saveDialog}>Save</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <ManualMergeDialog
        open={mergeOpen}
        entities={entities}
        onClose={() => setMergeOpen(false)}
        onSubmit={startManualMerge}
      />
      <MergeConfirm />
    </div>
  );
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

const moveBits = (m = {}) => {
  const bits = [];
  if (m.titled) bits.push(plural(m.titled, 'titled account'));
  if (m.ownership) bits.push(plural(m.ownership, 'ownership link'));
  if (m.stated_source) bits.push(plural(m.stated_source, 'stated-source link'));
  if (m.roles) bits.push(plural(m.roles, 'role'));
  return bits;
};

// Per-merge detail rendered inside the confirmation dialog (ConfirmBeforeActionDialog
// calls list_element with data={[payload]}). `payload` is the RESOLVED direction
// {keeper, duplicate, moves} after any user swap.
function MergeImpact({ data }) {
  const p = Array.isArray(data) ? data[0] : data;
  if (!p || !p.keeper) return null;
  const bits = moveBits(p.moves);
  return (
    <div className={styles.confirmDetail}>
      {bits.length ? `Moves ${bits.join(', ')} onto ` : 'Folds into '}
      <strong>{p.keeper.display_name}</strong>
      {p.keeper.case_role ? ` (${p.keeper.case_role})` : ''}.
    </div>
  );
}

// Likely-duplicate entities (e.g. an OCR account holder "CHRISTOPHER M HELD" and
// the Case Details party "Christopher Held") surfaced for a human-confirmed merge.
// Name canonicalization never merges :Entity nodes, so this is the entity-level
// dedup — and it stays human-gated because the data is forensic.
function DuplicateSuggestions({ suggestions, busyKey, onMerge, onDismiss }) {
  // Per-pair swap state: which side the user chose to KEEP, overriding the
  // server's proposed survivor. Keyed by the stable (sorted) id pair.
  const [swapped, setSwapped] = useState({});
  if (!suggestions.length) return null;
  return (
    <Card padding="md">
      <div className={styles.dupHeader}>
        <WarningIcon fontSize="small" />
        <span>Possible duplicate {suggestions.length === 1 ? 'entity' : 'entities'} ({suggestions.length})</span>
      </div>
      <div className={styles.dupSub}>
        These look like the same person recorded twice — typically a holder name lifted from a
        document and a person you later added in Case Details. Merging keeps one entity, moves the
        other’s accounts, ownership and roles onto it, and preserves the merged one as a hidden
        lineage record.
      </div>
      <ul className={styles.dupList}>
        {suggestions.map((s) => {
          const pairKey = [s.keeper.id, s.duplicate.id].sort().join('|');
          const isSwapped = !!swapped[pairKey];
          const keep = isSwapped ? s.duplicate : s.keeper;
          const dup = isSwapped ? s.keeper : s.duplicate;
          // The superseded side can't be a case-roster member, so a swap is only
          // offered when neither side is a participant (either may be the survivor).
          const canSwap = !s.keeper.is_case_party && !s.duplicate.is_case_party;
          const bits = moveBits(dup.moves);
          const busy = busyKey === pairKey;
          return (
            <li key={pairKey} className={styles.dupRow}>
              <div className={styles.dupText}>
                <div className={styles.dupNames}>
                  <span className={styles.dupDupName}>{dup.display_name}</span>
                  <KindChip kind={dup.kind} />
                  <span className={styles.dupArrow}>→</span>
                  <span className={styles.dupKeepName}>{keep.display_name}</span>
                  <KindChip kind={keep.kind} />
                  {keep.case_role && <Badge variant="default" size="sm">{keep.case_role}</Badge>}
                  {canSwap && (
                    <Button
                      size="small"
                      startIcon={<SwapHorizIcon fontSize="small" />}
                      disabled={busy}
                      onClick={() => setSwapped((prev) => ({ ...prev, [pairKey]: !prev[pairKey] }))}
                    >
                      Keep the other
                    </Button>
                  )}
                </div>
                <div className={styles.dupImpact}>
                  {bits.length
                    ? `Merging moves ${bits.join(', ')} onto “${keep.display_name}”.`
                    : 'Nothing is attached to the merged entity — it becomes a hidden alias.'}
                </div>
              </div>
              <div className={styles.dupActions}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy}
                  onClick={() => onMerge({ keeper: keep, duplicate: dup, moves: dup.moves, pairKey })}
                >
                  {busy ? 'Merging…' : 'Merge'}
                </Button>
                <Button size="small" disabled={busy} onClick={() => onDismiss(s)}>Not a duplicate</Button>
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

// User-initiated merge: pick the survivor + the entity to merge away, for a
// duplicate the detector didn't flag. A case participant can't be merged away
// (it stays the keeper), so it's excluded from the "merge & hide" picker.
function ManualMergeDialog({ open, entities, onClose, onSubmit }) {
  const [keepId, setKeepId] = useState('');
  const [dupId, setDupId] = useState('');
  useEffect(() => { if (open) { setKeepId(''); setDupId(''); } }, [open]);

  const dupCandidates = entities.filter((e) => !e.is_case_party && e.id !== keepId);
  const valid = keepId && dupId && keepId !== dupId;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Merge two entities</DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <div className={styles.detailMsg}>
          Use this when two entities are the same but weren’t flagged. The merged entity is hidden
          and preserved as a lineage record; its accounts, ownership and roles move to the kept one.
          A case participant can’t be merged away — keep it instead.
        </div>
        <FormControl fullWidth margin="dense">
          <InputLabel id="merge-keep-label">Keep (survivor)</InputLabel>
          <Select
            labelId="merge-keep-label"
            label="Keep (survivor)"
            value={keepId}
            onChange={(e) => { setKeepId(e.target.value); if (e.target.value === dupId) setDupId(''); }}
          >
            {entities.map((e) => <MenuItem key={e.id} value={e.id}>{e.display_name} ({e.kind})</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl fullWidth margin="dense" disabled={!keepId}>
          <InputLabel id="merge-dup-label">Merge &amp; hide</InputLabel>
          <Select
            labelId="merge-dup-label"
            label="Merge & hide"
            value={dupId}
            onChange={(e) => setDupId(e.target.value)}
          >
            {dupCandidates.map((e) => <MenuItem key={e.id} value={e.id}>{e.display_name} ({e.kind})</MenuItem>)}
          </Select>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!valid} onClick={() => onSubmit(keepId, dupId)}>Continue</Button>
      </DialogActions>
    </Dialog>
  );
}

function OwnershipDetail({ entity, entities, entitiesById, initialOwners, onSaveOwners, onSaveParties, onError }) {
  const isFiduciary = entity.kind === 'Trust' || entity.kind === 'Estate';
  const { data: serverParties } = useGetEntityParties(entity.id, isFiduciary);
  const { data: beneficial, mutate: refetchBeneficial } = useGetBeneficialOwners(entity.id);
  const roleSuggestions = (ROLES_BY_KIND[entity.kind] || []).map(([, label]) => label);
  const candidates = entities.filter((e) => e.id !== entity.id);

  const [rows, setRows] = useState(null);
  const [msg, setMsg] = useState(null);

  // One merged list. Each row = an entity, a free-text role (Trust/Estate only),
  // and a beneficial-ownership %. role -> :HAS_PARTY, % -> :OWNS (per entity).
  useEffect(() => {
    if (rows !== null) return;
    if (isFiduciary && !serverParties) return;
    const ownerPct = {};
    (initialOwners || []).forEach((o) => { ownerPct[o.owner_id] = o.pct; });
    let built = [];
    if (isFiduciary) {
      const seen = new Set();
      built = (serverParties || []).map((p) => {
        const pct = seen.has(p.entity_id) ? 0 : (ownerPct[p.entity_id] || 0);
        seen.add(p.entity_id);
        return { entity_id: p.entity_id, role: p.role, pct };
      });
      const partyIds = new Set((serverParties || []).map((p) => p.entity_id));
      (initialOwners || []).forEach((o) => {
        if (!partyIds.has(o.owner_id)) built.push({ entity_id: o.owner_id, role: '', pct: o.pct });
      });
    } else {
      built = (initialOwners || []).map((o) => ({ entity_id: o.owner_id, role: '', pct: o.pct }));
    }
    setRows(built);
  }, [serverParties, initialOwners, isFiduciary, rows]);

  const current = rows || [];
  // Beneficial ownership is per entity (one :OWNS edge) — collapse rows by entity.
  const pctByEntity = {};
  current.forEach((r) => {
    if (r.entity_id) pctByEntity[r.entity_id] = Math.max(pctByEntity[r.entity_id] || 0, Number(r.pct) || 0);
  });
  const total = Object.values(pctByEntity).reduce((s, p) => s + p, 0);
  const over = total > 1.0001;

  const addRow = () => setRows([...current, { entity_id: '', role: '', pct: 0 }]);
  const updateRow = (i, patch) => setRows(current.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const removeRow = (i) => setRows(current.filter((_, idx) => idx !== i));

  // Non-blocking: the cap table already reflects the local edits, so show "Saved."
  // immediately and persist in the background (owners revalidate the network;
  // parties patch the cache optimistically). A failure clears the note + toasts.
  const save = () => {
    setMsg(null);
    const owners = Object.entries(pctByEntity)
      .filter(([, p]) => p > 0).map(([entity_id, pct]) => ({ owner_id: entity_id, pct }));
    const ops = [onSaveOwners(owners)];
    if (isFiduciary) {
      const seen = new Set();
      const parties = current
        .filter((r) => r.entity_id && r.role && r.role.trim())
        .filter((r) => { const k = `${r.entity_id}|${r.role.trim()}`; if (seen.has(k)) return false; seen.add(k); return true; })
        .map((r) => ({ entity_id: r.entity_id, role: r.role.trim() }));
      ops.push(onSaveParties(parties));
    }
    setMsg('Saved.');
    Promise.all(ops)
      .then(() => refetchBeneficial())
      .catch((e) => { setMsg(null); onError?.(apiMessage(e)); });
  };

  return (
    <Card padding="lg">
      <div className={styles.detailHeader}>
        <span className={styles.detailTitle}>{entity.display_name}</span>
        <KindChip kind={entity.kind} />
      </div>
      {entity.dbas?.length > 0 && <div className={styles.entityDba}>d/b/a {entity.dbas.join(', ')}</div>}
      {/* Names it went by: a rename keeps the old name, a merge the merged-away entity's own name. */}
      {entity.akas?.length > 0 && <div className={styles.entityAka}>also known as {entity.akas.join(', ')}</div>}

      <div className={styles.subhead}>
        {isFiduciary
          ? 'Roles & beneficial ownership — total beneficial ≤ 100%'
          : 'Owners — cap table, total ≤ 100%'}
      </div>
      {current.map((r, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className={styles.ownerRow}>
          <FormControl size="small" className={styles.ownerSelect}>
            <Select value={r.entity_id} displayEmpty onChange={(e) => updateRow(i, { entity_id: e.target.value })}>
              <MenuItem value=""><em>Select…</em></MenuItem>
              {candidates.map((c) => <MenuItem key={c.id} value={c.id}>{c.display_name} ({c.kind})</MenuItem>)}
            </Select>
          </FormControl>
          {isFiduciary && (
            <Autocomplete
              freeSolo
              size="small"
              className={styles.roleSelect}
              options={roleSuggestions}
              value={r.role || ''}
              onInputChange={(e, val) => updateRow(i, { role: val })}
              renderInput={(params) => <TextField {...params} placeholder="role" aria-label="role" />}
            />
          )}
          <TextField
            size="small"
            type="number"
            className={styles.ownerPct}
            value={pctToInput(r.pct)}
            onChange={(e) => updateRow(i, { pct: inputToPct(e.target.value) })}
            inputProps={{ min: 0, max: 100, step: 1, 'aria-label': 'percent beneficial' }}
          />
          <span className={styles.pctSign}>%</span>
          <IconButton size="small" aria-label="Remove" onClick={() => removeRow(i)}><DeleteIcon fontSize="small" /></IconButton>
        </div>
      ))}

      <div className={styles.ownerFooter}>
        <Button size="small" startIcon={<AddIcon />} onClick={addRow}>{isFiduciary ? 'Add party' : 'Add owner'}</Button>
        <span className={over ? styles.totalOver : styles.total}>Beneficial total: {formatPercentage(total)}</span>
        <Button size="small" variant="contained" onClick={save} disabled={over}>Save</Button>
      </div>
      {over && <div className={styles.warn}><WarningIcon fontSize="small" /> Beneficial ownership exceeds 100% — reduce before saving.</div>}
      {msg && <div className={styles.detailMsg}>{msg}</div>}

      <div className={styles.subhead}>Ultimate beneficial owners</div>
      <BeneficialOwners result={beneficial} entitiesById={entitiesById} />
    </Card>
  );
}

function BeneficialOwners({ result, entitiesById }) {
  if (!result) return <div className={styles.hint}>Resolving…</div>;
  const owners = result.owners || [];
  return (
    <div>
      {result.has_cycle && (
        <div className={styles.warn}><WarningIcon fontSize="small" /> Cross-ownership cycle — part of the chain can’t be resolved automatically.</div>
      )}
      {result.depth_capped && (
        <div className={styles.warn}><WarningIcon fontSize="small" /> Ownership chain is very deep; resolution was capped.</div>
      )}
      {owners.length === 0 && <div className={styles.hint}>No resolved owners yet.</div>}
      <ul className={styles.beneficialList}>
        {owners.map((o) => (
          <li key={o.entity_id} className={styles.beneficialRow}>
            <span>{entitiesById[o.entity_id]?.display_name || o.entity_id}</span>
            <span className={styles.beneficialPct}>{formatPercentage(o.pct)}</span>
          </li>
        ))}
      </ul>
      {result.unresolved_pct > 0 && (
        <div className={styles.unresolved}>Unallocated / unknown: {formatPercentage(result.unresolved_pct)}</div>
      )}
    </div>
  );
}
